<template>
  <SafeTeleport v-if="sheet" to="body">
    <Drawer
      placement="bottom"
      :title="title"
      :aria-label="title"
      @hide-drawer="emit('close')"
    >
      <!-- Replaces Drawer's default title block so the subtitle sits with the
           heading rather than at the top of the scrolling body. Mirrors that
           default: same h5, and the mb-4 moves to the wrapper so the gap below
           the header is unchanged whether or not a subtitle is present. -->
      <template #title>
        <div class="mb-4 flex flex-col gap-0.5 pr-8">
          <h5
            class="inline-flex items-center text-base font-semibold text-gray-900"
          >
            {{ title }}
          </h5>
          <span
            v-if="subtitle"
            class="text-sm font-medium text-gray-600"
            data-cy="tapped-slot"
          >
            {{ subtitle }}
          </span>
        </div>
      </template>
      <div class="px-4 pb-4">
        <slot></slot>
      </div>
      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <slot name="delete"></slot>
          <SeraButton
            :disabled="saving"
            data-cy="modal-submit-btn"
            @click="handleSubmit"
          >
            <span v-if="!saving">{{ submitBtnText }}</span>
            <Spinner v-else :message="savingText"></Spinner>
          </SeraButton>
        </div>
      </template>
    </Drawer>
  </SafeTeleport>
  <Modal
    v-else
    :title="title"
    :submit-btn-text="submitBtnText"
    :saving="saving"
    :saving-text="savingText"
    @hide-modal="emit('close')"
    @submit="emit('submit')"
  >
    <slot></slot>
    <template #delete>
      <slot name="delete"></slot>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { onBeforeUnmount } from "vue";
import { SafeTeleport } from "vue-safe-teleport";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import Spinner from "@/components/Spinner.vue";

/**
 * Chooses the surface the create-appointment form is presented in: a bottom
 * sheet on a phone, the existing modal everywhere else. Mirrors
 * Scheduler/CalendarEventSurface.vue, and like it takes `sheet` from the call
 * site rather than deriving it — AppointmentCreateModal is reachable from the
 * classic board, Covered Tasks, Service Utilization and Check Availability, and
 * deriving placement from viewport width here would put a sheet on all of them.
 *
 * Unlike CalendarEventSurface, this one must NOT forward `#footer` to Modal.
 * The Schedule button and the notify toggle are fallback content of Modal's
 * `footer` slot, so a forwarding template suppresses both and silently removes
 * the desktop form's only way to submit. The sheet branch therefore rebuilds
 * that row rather than passing one through, and `#delete` is forwarded on its
 * own so the toggle keeps rendering inside Modal's own footer.
 *
 * `handleSubmit` mirrors Modal's 500ms submit debounce so the two branches
 * behave the same under a double tap, which is likelier on a phone than the
 * mouse it was originally written for. `trailing: false` is the difference:
 * lodash leaves the trailing edge on by default, so Modal's version emits a
 * second time 500ms later. Nothing books twice today only because the disabled
 * button swallows the second tap first.
 *
 * The sheet is teleported to `body` because a caller can sit inside a stacking
 * context: the phone scheduler's + button lives in a `fixed z-10` wrapper, which
 * traps a nested `z-[1003]` panel under a `z-20` sibling and hides the sheet
 * behind the timeline header. Modal teleports for the same reason, which is why
 * the dialog branch never had the problem.
 */
withDefaults(
  defineProps<{
    title?: string;
    // Shown under the heading in the sheet only; the dialog has no place for it.
    subtitle?: string;
    sheet?: boolean;
    saving?: boolean;
    savingText?: string;
    submitBtnText?: string;
  }>(),
  {
    title: "",
    subtitle: "",
    sheet: false,
    saving: false,
    savingText: "Saving...",
    submitBtnText: "Save"
  }
);

const emit = defineEmits<{ (e: "close"): void; (e: "submit"): void }>();

const handleSubmit = debounce(() => emit("submit"), 500, {
  leading: true,
  trailing: false
});

// The trailing half of the debounce outlives a sheet dismissed straight after a
// tap, and would emit once more into a closing instance.
onBeforeUnmount(() => handleSubmit.cancel());
</script>
